import {useEffect, useRef, useState} from "react";
import {useUser} from "UserProvider";
import {localStorageAsWritten, sessionStorageAsWritten} from "core/browserStorage";
import {fetchMarketingPreference, updateEmailConsent, updateMarketingPreference} from "./marketingApi";
import {subscribePendingEmailConsentGrant} from "./pendingEmailConsentGrant";
import {replayMarketingDecisions} from "./marketingDecisionReplay";
import {emailConsentIdentityChanged} from "./emailMarketingDecision";

/** Thin adapter over the single identity-bound coordinator; no independent grant/objection effects. */
export function useMarketingDecisionReplay(): void {
	const [user] = useUser();
	const accountId = user.isLoggedIn ? user.id : null;
	const previousIdentity = useRef(accountId);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => subscribePendingEmailConsentGrant(() => setAttempt(n => n + 1)), []);
	useEffect(() => {
		emailConsentIdentityChanged(previousIdentity.current, accountId);
		previousIdentity.current = accountId;
		// Where this page's writes went: the page-memory stand-ins when a press found the browser's storage
		// refused (a signup objection ticked here has to reach the account the press created,
		// kitecorp/transferify-ui#3503), else the browser's own. Read without a write probe, so a page load
		// with nothing to replay writes nothing.
		void replayMarketingDecisions(accountId, localStorageAsWritten(), sessionStorageAsWritten(), {
			readPreference: () => fetchMarketingPreference(accountId ?? undefined),
			submitGrant: ({noticeVersion, locale, expectedRevision, decisionReceipt, accountId: boundAccountId}) =>
				updateEmailConsent(true, noticeVersion, locale, expectedRevision, boundAccountId, decisionReceipt),
			submitObjection: ({policyVersion, accountId: boundAccountId}) =>
				updateMarketingPreference(true, policyVersion, boundAccountId),
		});
	}, [accountId, attempt]);
}
